import { useEffect, useState } from 'react';
import { Browser } from '@capacitor/browser';
import type { Theme } from '../lib/tokens';
import { FONTS } from '../lib/tokens';
import { getConsent, setConsent, onOpenConsentChoices, type Consent } from '../lib/analytics';

// Analytics consent. Shown once per device until answered; reopened from the
// You tab (Privacy choices). Accept and Decline are deliberately equal in size
// and placement — the ICO expects rejecting to be as easy as accepting.
export function ConsentBanner({ t }: { t: Theme }) {
  const [open, setOpen] = useState(() => getConsent() === null);

  useEffect(() => onOpenConsentChoices(() => setOpen(true)), []);

  if (!open) return null;

  const choose = (choice: Consent) => {
    setConsent(choice);
    setOpen(false);
  };

  const button = (filled: boolean) => ({
    all: 'unset' as const, cursor: 'pointer', flex: 1, boxSizing: 'border-box' as const,
    textAlign: 'center' as const, padding: '13px 16px', borderRadius: 100,
    fontFamily: FONTS.sans, fontSize: 14, fontWeight: 600,
    background: filled ? t.ink : 'transparent',
    color: filled ? t.paper : t.ink,
    border: `1.5px solid ${t.ink}`,
  });

  return (
    <div
      role="dialog"
      aria-label="Privacy choices"
      data-testid="consent-banner"
      style={{
        position: 'fixed', zIndex: 1000,
        left: 12, right: 12, bottom: 'calc(12px + env(safe-area-inset-bottom))',
        maxWidth: 440, margin: '0 auto',
        background: t.card, border: `1px solid ${t.line}`, borderRadius: 20,
        boxShadow: '0 12px 40px rgba(0,0,0,0.18)', padding: '18px 18px 16px',
      }}
    >
      <div style={{ fontFamily: FONTS.display, fontStyle: 'italic', fontSize: 21, color: t.ink, marginBottom: 6 }}>
        Help us improve Preminade
      </div>
      <p style={{ fontFamily: FONTS.sans, fontSize: 13, lineHeight: 1.5, color: t.inkSoft, margin: '0 0 14px' }}>
        Can we use analytics cookies and session recordings to see how the app is used?
        What you type, your messages and your identity details are always hidden.{' '}
        <span
          onClick={() => Browser.open({ url: 'https://preminade.com/privacy' })}
          style={{ color: t.terracotta, textDecoration: 'underline', cursor: 'pointer' }}
        >Privacy Policy</span>
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button data-testid="consent-decline" onClick={() => choose('denied')} style={button(false)}>Decline</button>
        <button data-testid="consent-accept" onClick={() => choose('granted')} style={button(true)}>Accept</button>
      </div>
    </div>
  );
}
